"use client";

import { useState } from "react";
import type { Conversation } from "@/lib/api/messages";
import {
  useAcceptRequest,
  useDeclineRequest,
} from "@/lib/hooks/useConversation";
import { ReportDialog } from "./ReportDialog";

/**
 * Acceptér / Afvis / Anmeld for a conversation the viewer has not yet
 * accepted. Rendered by RequestBanner, which stands in for the thread until
 * the decision is made.
 *
 * All three buttons are drawn from `can`, never from `isRequest`: accepting an
 * already-accepted conversation is a 200 that changes nothing, so `can.accept`
 * is true for any live seat — and a `can` that said false for an operation the
 * route accepts is exactly the divergence D11 exists to stop.
 */
export function RequestActions({
  conversation,
}: {
  conversation: Conversation;
}) {
  const [error, setError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  const accept = useAcceptRequest(conversation.id);
  const decline = useDeclineRequest(conversation.id);

  const sender = conversation.createdBy;

  async function handleAccept() {
    setError(null);
    try {
      await accept.mutateAsync();
    } catch {
      setError("Anmodningen kunne ikke accepteres. Prøv igen.");
    }
  }

  async function handleDecline() {
    if (!confirm("Afvis anmodningen? Du kan ikke se den igen.")) return;
    setError(null);
    try {
      await decline.mutateAsync();
    } catch {
      setError("Anmodningen kunne ikke afvises. Prøv igen.");
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleAccept}
          disabled={!conversation.can.accept || accept.isPending}
          className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {accept.isPending ? "Accepterer…" : "Acceptér"}
        </button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={!conversation.can.decline || decline.isPending}
          className="px-3 py-1.5 text-sm font-medium text-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          {decline.isPending ? "Afviser…" : "Afvis"}
        </button>
        {conversation.can.report && (
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="px-3 py-1.5 text-sm font-medium text-destructive rounded-xl hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
          >
            Anmeld
          </button>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {reportOpen && (
        <ReportDialog
          conversationId={conversation.id}
          userId={sender?.id}
          defaultBlock
          onClose={() => setReportOpen(false)}
        />
      )}
    </>
  );
}
